import { uuid } from "vue-uuid";
import api from "@/services/api";
import { mainServices } from "@/services/mainServices";
const prefixed = api;
const { post } = prefixed.getPrefixedMethods("intake");
const requirements = prefixed.getPrefixedMethods("requirements");
export const BENEFIT_TYPE_ITEMS = [
    { title: "Pharmacy benefit", value: "pharmacy_benefits" },
    { title: "Medical benefit", value: "medical_benefits" },
];
const REFUSALS = {
    locked: "This patient is not on a case assigned to you. Ask your lead to assign the patient to you.",
    ambiguous: "More than one patient matches these details. Enter the patient's MRN to choose one.",
    dob_mismatch: "This MRN belongs to a patient with another date of birth. Check the MRN and the date of birth.",
    mrn_mismatch: "A patient with this name and date of birth has another MRN. Check the MRN.",
    standalone_steps_off: "Starting a review without a fax is turned off for this company. Reload the page.",
    step_off: "This step is turned off for this company. Reload the page.",
    request_key_reused: "The details changed after the last submit. Submit again.",
    conflict: "The patient list changed while saving. Submit again.",
    not_found: "The patient from the network search is no longer available. Search the network again.",
    admin_company: "Switch to a client company to start a review.",
    coverage_not_found: "This coverage is no longer on the patient's file. Choose another coverage.",
    pa_not_found: "This prior authorization is not available to you. Reload the page.",
    pa_has_order: "This prior authorization came from an intake order. Work its medical necessity on that order.",
    pa_case_pending: "This prior authorization is still being set up. Try again in a minute.",
    pa_patient_unknown: "Link this prior authorization to a patient first.",
    pa_coverage_missing: "Add the member ID and the benefit type to this prior authorization first.",
};
const SERVER_COPY = new Set(["step_conflict", "step_refused", "incomplete"]);
const FIELD_LABELS = {
    "patient.first_name": "Patient first name",
    "patient.last_name": "Patient last name",
    "patient.mrn": "Patient MRN",
    "coverage.member_id": "Member ID",
    "order.drug_name": "Drug name",
    "order.diagnosis_icd10_code": "Diagnosis code",
    "order.patient_gender": "Gender",
    "order.patient_address": "Address",
    "order.patient_city": "City",
    "order.patient_state": "State",
    "order.patient_zip": "ZIP",
    "documents.links": "Documents",
};
const TOO_LONG = /^([\w.]+?)(?:\.\d+)?: (?:String|List) should have at most (\d+) (characters?|items?)/;
function lengthMessage(message) {
    const match = TOO_LONG.exec(message);
    const label = match && FIELD_LABELS[match[1]];
    if (!match || !label)
        return message;
    return match[3].startsWith("item")
        ? `${label}: attach ${match[2]} or fewer.`
        : `${label} is too long. Use ${match[2]} characters or fewer.`;
}
function sentences(message) {
    return message
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => `${part[0].toUpperCase()}${part.slice(1)}.`)
        .join(" ");
}
export function manualEntryStartErrors(error, overrides = {}) {
    const data = error?.response?.data;
    if (!data)
        return [];
    const code = data.error_code;
    const copy = code && (overrides[code] ?? REFUSALS[code]);
    if (copy)
        return [copy];
    if (code === "coverage_refused")
        return [`Check the insurance. ${data.error_details}.`];
    if (code && SERVER_COPY.has(code) && data.error_details)
        return [sentences(data.error_details)];
    if (Array.isArray(data.validation_errors))
        return data.validation_errors.map((item) => lengthMessage(String(item)));
    return data.error_details ? [data.error_details] : [];
}
export function startNotes(result, work) {
    const name = [result.patient.firstName, result.patient.lastName].filter(Boolean).join(" ") || "the patient";
    return [
        ...(result.confirm.includes("name")
            ? [`Check the patient. The ${work} is on ${name}, and that name is not the name you typed.`]
            : []),
        ...(result.confirm.includes("mrn") ? [`Check the patient. ${name} does not have the MRN you typed.`] : []),
        ...(result.openCase ? [`This patient has another open case. Make sure this ${work} is not a duplicate.`] : []),
    ];
}
export function documentsMissing(body, result) {
    const sent = body.documents.links.length + body.documents.networkDocumentIds.length;
    return sent > 0 && !result.documentsFiled;
}
/** One request key per body: a retry of the same body keeps its key, a changed body gets a new one. */
export class RequestKey {
    attempt = null;
    for(body) {
        const signature = JSON.stringify(body);
        if (this.attempt?.signature !== signature)
            this.attempt = { key: uuid.v4(), signature };
        return this.attempt.key;
    }
    reset() {
        this.attempt = null;
    }
}
export async function searchPayers(companyId, value) {
    if (!companyId)
        return [];
    const response = (await mainServices.getPayorNamesByName(companyId, value));
    return response.results.map((payer) => ({
        title: [payer.payorName, payer.bin && `BIN ${payer.bin}`, payer.pcn && `PCN ${payer.pcn}`]
            .filter(Boolean)
            .join(" · "),
        value: String(payer.id),
    }));
}
export async function searchIcd10Codes(value) {
    if (value.length > 8)
        return [];
    const response = await requirements.get("search-icd10", {
        params: { q: value },
    });
    return response.results.map((code) => ({ title: `${code.code} - ${code.name}`, value: code.code }));
}
export const manualEntryStartService = {
    startBv(requestKey, body) {
        return post("manual-entry-starts/", { requestKey, step: "bv", ...body });
    },
    startMn(requestKey, body) {
        return post("manual-entry-starts/", { requestKey, step: "mn", ...body });
    },
    startMnOnPa(requestKey, orderKey) {
        return post("manual-entry-starts/", { requestKey, step: "mn_on_pa", pa: { orderKey } });
    },
};
